// src/pages/DataManager.js
// Combines DataImport + LogbookExport into one tabbed page
import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload, Download, AlertCircle, CheckCircle, Database, FileText,
  CalendarDays, Plane, FileSpreadsheet, X, Clock, MapPin, Trash2,
  Eye, Loader2, Shield, Copy, ArrowDownToLine,
  ArrowUpFromLine, BookOpen
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

// ─── Schedule Import Preview Modal ───────────────────────────────────────────
const ScheduleImportModal = ({ isOpen, onClose, parsedFlights, onImport }) => {
  const [selectedFlights, setSelectedFlights] = useState(parsedFlights.map((_, i) => i));
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    setSelectedFlights(parsedFlights.map((_, i) => i));
  }, [parsedFlights]);

  if (!isOpen) return null;

  const toggleFlight = (index) => {
    setSelectedFlights(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index].sort()
    );
  };

  const handleImport = async () => {
    setImporting(true);
    await onImport(selectedFlights.map(i => parsedFlights[i]));
    setImporting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown Date';
    if (dateStr.match(/^\d{2}[A-Z]{3}\d{4}$/)) {
      const monthMap = {
        JAN:'01',FEB:'02',MAR:'03',APR:'04',MAY:'05',JUN:'06',
        JUL:'07',AUG:'08',SEP:'09',OCT:'10',NOV:'11',DEC:'12'
      };
      const d = new Date(parseInt(dateStr.slice(5)), parseInt(monthMap[dateStr.slice(2,5)])-1, parseInt(dateStr.slice(0,2)));
      return d.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
    }
    return dateStr;
  };

  const formatBlockTime = (h) => {
    if (!h) return '0:00';
    const hrs = Math.floor(h), mins = Math.round((h - hrs) * 60);
    return `${hrs}:${String(mins).padStart(2,'0')}`;
  };

  const groupedFlights = parsedFlights.reduce((acc, flight, i) => {
    const d = flight.date || 'Unknown Date';
    if (!acc[d]) acc[d] = [];
    acc[d].push({ ...flight, originalIndex: i });
    return acc;
  }, {});

  const totalSelectedTime = selectedFlights.reduce((t, i) => t + (parsedFlights[i]?.blockTime || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <CalendarDays className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Review Schedule Import</h2>
              <p className="text-sm text-gray-600">
                {parsedFlights.length} flights found · {selectedFlights.length} selected · {formatBlockTime(totalSelectedTime)} total time
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => setSelectedFlights(parsedFlights.map((_,i)=>i))} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Select All</button>
            <span className="text-gray-300">·</span>
            <button onClick={() => setSelectedFlights([])} className="text-sm text-gray-600 hover:text-gray-700 font-medium">Deselect All</button>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span>Click flights to toggle selection</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedFlights).length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No flights found. Check the format and try again.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedFlights).map(([date, flights]) => (
                <div key={date} className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CalendarDays className="h-4 w-4 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">{formatDate(date)}</h3>
                    <span className="text-sm text-gray-500">({flights.length} flight{flights.length !== 1 ? 's' : ''})</span>
                  </div>
                  <div className="grid gap-3">
                    {flights.map((flight) => {
                      const isSelected = selectedFlights.includes(flight.originalIndex);
                      return (
                        <div
                          key={flight.originalIndex}
                          onClick={() => toggleFlight(flight.originalIndex)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                              </div>
                              <div className="flex items-center space-x-3">
                                <Plane className="h-4 w-4 text-gray-500" />
                                <span className="font-semibold text-gray-900">{flight.flightNumber}</span>
                                <div className="flex items-center space-x-1 text-gray-600">
                                  <MapPin className="h-3 w-3" />
                                  <span className="text-sm">{flight.origin} → {flight.destination}</span>
                                </div>
                                <div className="flex items-center space-x-1 text-gray-600">
                                  <Clock className="h-3 w-3" />
                                  <span className="text-sm">{flight.scheduledOut} - {flight.scheduledIn}</span>
                                </div>
                                <span className="text-sm text-gray-600">{formatBlockTime(flight.blockTime)}</span>
                                {flight.trip && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Trip {flight.trip}</span>}
                                <span className={`text-xs px-2 py-1 rounded border ${
                                  flight.type === 'training'
                                    ? 'bg-orange-100 text-orange-800 border-orange-200'
                                    : 'bg-blue-100 text-blue-800 border-blue-200'
                                }`}>
                                  {flight.type === 'training' ? 'Training' : 'Flight'}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleFlight(flight.originalIndex); }}
                              className={`p-1 rounded hover:bg-red-100 transition-colors ${isSelected ? 'text-gray-400' : 'text-red-500'}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedFlights.length > 0 && (
              <>Importing {selectedFlights.length} of {parsedFlights.length} flights <span className="font-medium">({formatBlockTime(totalSelectedTime)} total time)</span></>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} disabled={importing} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={selectedFlights.length === 0 || importing}
              className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                selectedFlights.length === 0 || importing ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {importing ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Importing...</span></>
              ) : (
                <><CheckCircle className="h-4 w-4" /><span>Import {selectedFlights.length} Flight{selectedFlights.length !== 1 ? 's' : ''}</span></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { id: 'import',       label: 'Import',         icon: ArrowUpFromLine },
  { id: 'data-export',  label: 'Data Export',    icon: ArrowDownToLine },
  { id: 'logbook',      label: 'Logbook Export', icon: BookOpen },
  { id: 'ffdo',         label: 'FFDO Export',    icon: Shield },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const authToken = () => localStorage.getItem('authToken') || localStorage.getItem('token');

const DataManager = () => {
  const [activeTab, setActiveTab] = useState('import');

  // ── Import state ──
  const [importing, setImporting]           = useState(false);
  const [importResults, setImportResults]   = useState(null);
  const [activeImport, setActiveImport]     = useState(null);
  const [scheduleText, setScheduleText]     = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [parsedFlights, setParsedFlights]   = useState([]);
  const [parsedTrips, setParsedTrips]       = useState([]);

  // ── Data Export state ──
  const [exporting, setExporting]           = useState(false);
  const [activeExport, setActiveExport]     = useState(null);

  // ── Logbook Export state ──
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');
  const [format, setFormat]                 = useState('jeppesen');
  const [isGenerating, setIsGenerating]     = useState(false);
  const [isPreviewing, setIsPreviewing]     = useState(false);
  const [previewData, setPreviewData]       = useState(null);
  const [dateRange, setDateRange]           = useState(null);

  // ── FFDO state ──
  const [isGeneratingFfdo, setIsGeneratingFfdo] = useState(false);
  const [ffdoStartDate, setFfdoStartDate]   = useState(() => `${new Date().getFullYear()}-01-01`);
  const [ffdoEndDate, setFfdoEndDate]       = useState(() => `${new Date().getFullYear()}-12-31`);
  const [showFfdoModal, setShowFfdoModal]   = useState(false);
  const [ffdoLines, setFfdoLines]           = useState([]);

  // ── Auth helper ──
  const getAuthHeaders = () => ({
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      'Content-Type': 'application/json'
    }
  });

  // ─── Logbook export handlers ──────────────────────────────────────────────
  const fetchDateRange = useCallback(async () => {
    try {
      const res = await axios.get('/api/export/date-range', {
        headers: { 'Authorization': `Bearer ${authToken()}` }
      });
      if (res.data.success) {
        setDateRange(res.data.data);
        const y = new Date().getFullYear();
        setStartDate(`${y}-01-01`);
        setEndDate(`${y}-12-31`);
      }
    } catch (e) {
      console.error('Error fetching date range:', e);
    }
  }, []);

  useEffect(() => { fetchDateRange(); }, [fetchDateRange]);

  const handlePreview = async () => {
    if (!startDate || !endDate) return toast.error('Please select start and end dates');
    setIsPreviewing(true);
    try {
      const res = await axios.post('/api/export/preview', { startDate, endDate }, {
        headers: { 'Authorization': `Bearer ${authToken()}` }
      });
      if (res.data.success) { setPreviewData(res.data.data); toast.success('Preview loaded'); }
    } catch (e) {
      toast.error('Failed to preview logbook data');
    } finally { setIsPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate) return toast.error('Please select start and end dates');
    if (new Date(startDate) > new Date(endDate)) return toast.error('Start date must be before end date');
    setIsGenerating(true);
    const tid = toast.loading('Generating logbook PDF...');
    try {
      const res = await axios.post('/api/export/generate', { startDate, endDate, format }, {
        headers: { 'Authorization': `Bearer ${authToken()}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `Pilot_Logbook_${startDate}_to_${endDate}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Logbook PDF downloaded', { id: tid });
    } catch (e) {
      toast.error('Failed to generate logbook PDF', { id: tid });
    } finally { setIsGenerating(false); }
  };

  const handleFfdoExport = async () => {
    if (!ffdoStartDate || !ffdoEndDate) return toast.error('Please select FFDO date range');
    if (new Date(ffdoStartDate) > new Date(ffdoEndDate)) return toast.error('Start date must be before end date');
    setIsGeneratingFfdo(true);
    try {
      const res = await axios.get('/api/export/ffdo', {
        params: { startDate: ffdoStartDate, endDate: ffdoEndDate },
        headers: { 'Authorization': `Bearer ${authToken()}` },
        responseType: 'text'
      });
      setFfdoLines(res.data.split('\n').map(l => l.trim()).filter(Boolean));
      setShowFfdoModal(true);
    } catch (e) {
      if (e.response?.status === 404) toast.error('No flights with flight numbers found in that date range');
      else toast.error('Failed to generate FFDO export');
    } finally { setIsGeneratingFfdo(false); }
  };

  const handleFfdoCopy = () => {
    navigator.clipboard.writeText(ffdoLines.join('\n'));
    toast.success('Copied to clipboard');
  };

  const handleFfdoDownload = () => {
    const blob = new Blob([ffdoLines.join('\n') + '\n'], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `FFDO_${ffdoStartDate}_to_${ffdoEndDate}.txt`);
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const formatHours = (h) => h ? parseFloat(h).toFixed(1) : '0.0';

  // ─── Import handlers ──────────────────────────────────────────────────────
  const parseScheduleText = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    const flights = [], trips = [];
    let currentDate = null, currentTrip = null, currentTripData = null;

    for (const line of lines) {
      const t = line.trim();
      if (!t) continue;

      const mainDateMatch = t.match(/^(\d{2}[A-Z]{3}\d{4})\s+(\d{2}:\d{2})/);
      if (mainDateMatch) { currentDate = mainDateMatch[1]; continue; }

      const tripMatch = t.match(/Trip\s+(\d+)\s+TAFB:\s+(\d+)\s+d\s+\(([^)]+)\)\s+Rest end:\s+(\d{2}[A-Z]{3}\d{4})\s+(\d{2}:\d{2})\s+Sch\/Pay:\s+([^/]+)\s+\/\s+(.+)/);
      if (tripMatch) {
        currentTrip = tripMatch[1];
        const restOfLine = tripMatch[7];
        currentTripData = {
          tripNumber: currentTrip, tafbDays: parseInt(tripMatch[2]), tafbHours: tripMatch[3],
          restEndDate: tripMatch[4], restEndTime: tripMatch[5], scheduledTime: tripMatch[6]?.trim(),
          payTime: null, onDuty: null, offDuty: null, totalDutyTime: null
        };
        const tdm = restOfLine.match(/Total Duty:\s+(\d{1,2}:\d{2})/);
        if (tdm) {
          currentTripData.totalDutyTime = tdm[1];
          const pm = restOfLine.match(/^([^T]+?)(?:\s+Total Duty:)/);
          if (pm) currentTripData.payTime = pm[1].trim();
          trips.push(currentTripData);
        } else {
          currentTripData.payTime = restOfLine.trim();
        }
        continue;
      }

      const totalDutyMatch = t.match(/Total Duty:\s+(\d{1,2}:\d{2})/);
      if (totalDutyMatch && currentTripData && !currentTripData.totalDutyTime) {
        currentTripData.totalDutyTime = totalDutyMatch[1];
        trips.push(currentTripData);
        continue;
      }

      const dutyDateMatch = t.match(/Duty\s+(\d{2}[A-Z]{3}\d{4})\s+(\d{2}:\d{2})-(\d{2}:\d{2})/);
      if (dutyDateMatch) {
        currentDate = dutyDateMatch[1];
        if (currentTripData && !currentTripData.onDuty) currentTripData.onDuty = `${dutyDateMatch[1]} ${dutyDateMatch[2]}`;
        continue;
      }

      const flightMatch = t.match(/^(\d+)\s+([A-Z]{3})-([A-Z]{3})\s+(\d{2}:\d{2})-(\d{2}:\d{2})\s+BT:\s+(\d{2}:\d{2})/);
      if (flightMatch && currentDate) {
        const [, fn, dep, arr, depT, arrT, bt] = flightMatch;
        const [h, m] = bt.split(':').map(Number);
        flights.push({
          date: currentDate, flightNumber: `AS${fn}`, origin: dep, destination: arr,
          scheduledOut: depT, scheduledIn: arrT, blockTime: h + m/60,
          tripNumber: currentTrip, trip: currentTrip, type: 'flight', originalLine: t
        });
      }
    }
    return { flights, trips };
  };

  const handleExcelExport = async (dataType, endpoint, filename) => {
    setExporting(true); setActiveExport(dataType);
    try {
      const token = authToken();
      if (!token) return toast.error('Authentication required');
      const res = await axios.get(endpoint, {
        responseType: 'blob', timeout: 60000,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${dataType} exported successfully!`);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) toast.error('Authentication failed. Please log in again.');
      else toast.error(`Export failed: ${e.response?.data?.message || e.message}`);
    } finally { setExporting(false); setActiveExport(null); }
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!['xls','xlsx'].includes(file.name.split('.').pop().toLowerCase())) {
      toast.error('Please select an Excel file (.xls or .xlsx)');
      event.target.value = ''; return;
    }
    try {
      setImporting(true); setActiveImport('Flight Logs (Excel)'); setImportResults(null);
      const formData = new FormData();
      formData.append('excelFile', file);
      const res = await axios.post('/api/flights/import-excel', formData, getAuthHeaders());
      setImportResults(res.data);
      toast.success(`Excel import completed! ${res.data.imported} flights imported.`);
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast.error(`Failed to import Excel: ${msg}`);
      setImportResults({ success: false, imported: 0, errors: 1, errorDetails: [msg] });
    } finally { setImporting(false); setActiveImport(null); event.target.value = ''; }
  };

  const handleFileUpload = async (event, endpoint, dataType, expectedHeaders) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      setImporting(true); setActiveImport(dataType); setImportResults(null);
      const text = await file.text();
      const cleanText = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n');
      const lines = cleanText.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('File is empty or missing data rows.'); return; }
      const headers = lines[0].split('\t').map(h => h.trim());
      if (dataType === 'Flight Logs') {
        const required = ['flight_flightDate','flight_flightNumber'];
        if (required.filter(r => headers.some(h => h.includes(r))).length < required.length) {
          toast.error('Missing required flight log columns.'); return;
        }
      } else {
        const missing = expectedHeaders.filter(h => !headers.includes(h));
        if (missing.length > 0) { toast.error(`Missing headers: ${missing.join(', ')}`); return; }
      }
      const data = lines.slice(1)
        .map(line => {
          const vals = line.split('\t');
          const row = {};
          headers.forEach((h, i) => { row[h] = vals[i]?.trim() || ''; });
          return row;
        })
        .filter(row => {
          if (dataType === 'Flight Logs') {
            return row['flight_flightNumber'] || row[' flight_flightNumber'] ||
                   row['flight_flightDate']   || row[' flight_flightDate']   ||
                   row['flight_totalTime']    || row[' flight_totalTime'];
          }
          return row['Aircraft ID'] !== 'AIRCRAFT ID';
        });
      if (!data.length) { toast.error('No valid data rows found.'); return; }
      const res = await axios.post(endpoint, { data }, getAuthHeaders());
      setImportResults(res.data);
      toast.success(`${dataType} import completed! ${res.data.imported} records imported.`);
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast.error(`Failed to import ${dataType}: ${msg}`);
      setImportResults({ success: false, imported: 0, errors: 1, errorDetails: [msg] });
    } finally { setImporting(false); setActiveImport(null); event.target.value = ''; }
  };

  const handleScheduleImport = async () => {
    if (!scheduleText.trim()) return toast.error('Please paste your schedule data.');
    try {
      const { flights, trips } = parseScheduleText(scheduleText);
      if (!flights.length) return toast.error('No flights found. Check the format.');
      setParsedFlights(flights); setParsedTrips(trips); setShowPreviewModal(true);
    } catch (e) { toast.error('Failed to parse schedule data.'); }
  };

  const handleConfirmedImport = async (selectedFlights) => {
    try {
      setImporting(true); setActiveImport('Schedule'); setImportResults(null);
      const res = await axios.post('/api/flights/import-schedule', {
        scheduleText, parsedFlights: selectedFlights, parsedTrips
      }, getAuthHeaders());
      setImportResults(res.data);
      toast.success(`${res.data.imported} flights imported successfully!`);
      setShowPreviewModal(false);
      setScheduleText('');
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast.error(`Failed to import schedule: ${msg}`);
      setImportResults({ success: false, imported: 0, errors: 1, errorDetails: [msg] });
    } finally { setImporting(false); setActiveImport(null); }
  };

  const downloadTemplate = (name, headers) => {
    const blob = new Blob([headers.join('\t') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${name}_template.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${name} template downloaded`);
  };

  const importConfigs = [
    {
      title: 'Flight Logs', description: 'Import from Log Ten Pro (tab-separated)',
      endpoint: '/api/flights/import', exportEndpoint: '/api/flights/export',
      dataType: 'Flight Logs', templateName: 'flight_logs', exportFilename: 'flight_logs',
      headers: ['flight_flightDate','flight_flightNumber','flight_from','flight_to','aircraft_aircraftID','aircraftType_type','flight_scheduledDepartureTime','flight_scheduledArrivalTime','flight_actualDepartureTime','flight_actualArrivalTime','flight_totalTime','flight_pic','flight_sic','flight_night','flight_actualInstrument','flight_dualReceived','flight_dualGiven','flight_dayLandings','flight_dayTakeoffs','flight_nightLandings','flight_nightTakeoffs','flight_selectedCrewPIC','flight_selectedCrewSIC','flight_selectedApproach1','flight_remarks'],
      icon: Plane, color: 'blue', hasExcelOption: true
    },
    {
      title: 'Aircraft Types', description: 'Import aircraft type database',
      endpoint: '/api/aircraft-types/import', exportEndpoint: '/api/aircraft-types/export',
      dataType: 'Aircraft Types', templateName: 'aircraft_types', exportFilename: 'aircraft_types',
      headers: ['Type','Make','Model','Engine Type','Category','Class','Notes'],
      icon: Database, color: 'green'
    },
    {
      title: 'Aircraft', description: 'Import your aircraft fleet',
      endpoint: '/api/aircraft/import', exportEndpoint: '/api/aircraft/export',
      dataType: 'Aircraft', templateName: 'aircraft', exportFilename: 'aircraft',
      headers: ['Aircraft ID','Type','Make','Model','Year','Engine Type','Complex','High Performance','Retractable','EFIS','Notes'],
      icon: Upload, color: 'purple'
    },
    {
      title: 'Crew / Address Book', description: 'Import pilots and crew members',
      endpoint: '/api/crew/import', exportEndpoint: '/api/crew/export',
      dataType: 'Crew', templateName: 'crew', exportFilename: 'crew',
      headers: ['Name','Full Name','Organization','Type','Email','Phone','Comment'],
      icon: FileText, color: 'orange'
    },
    {
      title: 'Airports / Places', description: 'Import airport database',
      endpoint: '/api/airports/import', exportEndpoint: '/api/airports/export',
      dataType: 'Airports', templateName: 'airports', exportFilename: 'airports',
      headers: ['ICAO ID','IATA ID','Name','City','Country','Province','Lat','Lon','Elevation','Magnetic Variation','Beacon','Notes'],
      icon: Database, color: 'yellow'
    }
  ];

  // ─── Date preset helper ───────────────────────────────────────────────────
  const datePresets = (setStart, setEnd) => [
    { label: 'This Year',    fn: () => { const y=new Date().getFullYear(); setStart(`${y}-01-01`); setEnd(`${y}-12-31`); } },
    { label: 'Last Year',    fn: () => { const y=new Date().getFullYear()-1; setStart(`${y}-01-01`); setEnd(`${y}-12-31`); } },
    { label: 'Last 3 Mo',    fn: () => { const e=new Date(), s=new Date(); s.setMonth(s.getMonth()-3); setStart(s.toISOString().split('T')[0]); setEnd(e.toISOString().split('T')[0]); } },
    { label: 'All Time',     fn: () => { if (dateRange) { setStart(dateRange.firstFlight.split('T')[0]); setEnd(dateRange.lastFlight.split('T')[0]); } } },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* FFDO Modal */}
      {showFfdoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-blue-700" />
                <h2 className="text-lg font-semibold text-gray-900">FFDO Flight Hours</h2>
                <span className="text-sm text-gray-500">— {ffdoLines.length} flights</span>
              </div>
              <button onClick={() => setShowFfdoModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <pre className="font-mono text-sm text-gray-800 whitespace-pre leading-relaxed">{ffdoLines.join('\n')}</pre>
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t gap-3">
              <p className="text-xs text-gray-400">{ffdoStartDate} → {ffdoEndDate}</p>
              <div className="flex space-x-2">
                <button onClick={handleFfdoCopy} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                  <Copy className="h-4 w-4 mr-1.5" />Copy
                </button>
                <button onClick={handleFfdoDownload} className="inline-flex items-center px-4 py-2 bg-blue-700 text-white rounded-md text-sm hover:bg-blue-800">
                  <Download className="h-4 w-4 mr-1.5" />Download .txt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="aviation-gradient p-2 rounded-lg">
            <Database className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Data Manager</h1>
        </div>
        <p className="text-gray-600">Import data, export backups, and generate logbook reports.</p>
        {dateRange && (
          <p className="text-sm text-blue-700 mt-1">
            {dateRange.totalFlights} flights recorded · {new Date(dateRange.firstFlight).toLocaleDateString()} – {new Date(dateRange.lastFlight).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Tab bar */}
      <div className="mb-8">
        <div className="inline-flex w-full sm:w-auto bg-gray-100 rounded-xl p-1 gap-1 flex-wrap sm:flex-nowrap">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 flex-1 sm:flex-none justify-center sm:justify-start ${
                activeTab === id
                  ? 'bg-white text-aviation-700 shadow-sm ring-1 ring-black ring-opacity-5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${activeTab === id ? 'text-aviation-600' : ''}`} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB: Import ─────────────────────────────────────────────────── */}
      {activeTab === 'import' && (
        <div className="space-y-8">
          {/* Schedule import */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 rounded-lg bg-orange-100 text-orange-600">
                <CalendarDays className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Alaska Airlines Schedule Import</h3>
                <p className="text-sm text-gray-600">Paste flight schedule text from CrewAccess</p>
              </div>
            </div>
            <div className="space-y-4">
              <textarea
                className="w-full h-64 p-4 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-aviation-500 resize-none"
                placeholder="Paste your schedule data here..."
                value={scheduleText}
                onChange={e => setScheduleText(e.target.value)}
                disabled={importing}
              />
              <button
                onClick={handleScheduleImport}
                disabled={importing}
                className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {importing && activeImport === 'Schedule' ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Importing...</span></>
                ) : (
                  <><Upload className="h-4 w-4" /><span>Preview & Import Schedule</span></>
                )}
              </button>
            </div>
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Instructions</h4>
              <ol className="text-sm text-gray-700 space-y-1">
                <li>1. Copy your schedule from the CrewAccess roster website</li>
                <li>2. Paste into the box above</li>
                <li>3. Click Preview to review and select flights before importing</li>
              </ol>
              {scheduleText && (
                <div className="mt-3">
                  <button
                    onClick={() => {
                      const { flights } = parseScheduleText(scheduleText);
                      if (flights.length) { setParsedFlights(flights); setShowPreviewModal(true); }
                      else toast.error('No flights found in the schedule text');
                    }}
                    className="btn-secondary text-sm"
                    disabled={importing}
                  >
                    Quick Preview ({parseScheduleText(scheduleText).flights.length} flights found)
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Log Ten Pro file imports */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Log Ten Pro Import</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {importConfigs.map((config) => {
                const Icon = config.icon;
                const isActive = activeImport === config.dataType || activeImport === `${config.dataType} (Excel)`;
                return (
                  <div key={config.dataType} className="card hover:shadow-lg transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg bg-${config.color}-100 text-${config.color}-600`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{config.title}</h3>
                        <p className="text-sm text-gray-600 mb-4">{config.description}</p>
                        <div className="space-y-3">
                          <button
                            onClick={() => downloadTemplate(config.templateName, config.headers)}
                            disabled={importing}
                            className="w-full btn-secondary flex items-center justify-center space-x-2"
                          >
                            <Download className="h-4 w-4" /><span>Download Template</span>
                          </button>
                          {config.hasExcelOption && (
                            <label className={`w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2 cursor-pointer transition-colors ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              {activeImport === 'Flight Logs (Excel)' ? (
                                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Importing Excel...</span></>
                              ) : (
                                <><FileSpreadsheet className="h-4 w-4" /><span>Import Excel Logbook</span></>
                              )}
                              <input type="file" accept=".xls,.xlsx" onChange={handleExcelUpload} className="hidden" disabled={importing} />
                            </label>
                          )}
                          <label className={`w-full btn-primary flex items-center justify-center space-x-2 cursor-pointer ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {isActive && activeImport === config.dataType ? (
                              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Importing...</span></>
                            ) : (
                              <><Upload className="h-4 w-4" /><span>Import {config.hasExcelOption ? 'Tab File' : config.title}</span></>
                            )}
                            <input type="file" accept=".txt,.tsv,.csv" onChange={e => handleFileUpload(e, config.endpoint, config.dataType, config.headers)} className="hidden" disabled={importing} />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Import results */}
          {importResults && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                {importResults.success && importResults.errors === 0
                  ? <CheckCircle className="h-5 w-5 text-green-600" />
                  : <AlertCircle className="h-5 w-5 text-yellow-600" />}
                <h3 className="text-lg font-bold text-gray-900">Import Results</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{importResults.imported || 0}</div>
                  <div className="text-sm text-green-700">Imported</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{importResults.errors || 0}</div>
                  <div className="text-sm text-yellow-700">Errors / Skipped</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{(importResults.imported||0)+(importResults.errors||0)}</div>
                  <div className="text-sm text-blue-700">Total Processed</div>
                </div>
                {importResults.totalTime && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{importResults.totalTime}</div>
                    <div className="text-sm text-purple-700">Hours Imported</div>
                  </div>
                )}
              </div>
              {importResults.errorDetails?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {importResults.errorDetails.map((e, i) => <div key={i} className="text-sm text-red-700 mb-1">{e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Data Export ─────────────────────────────────────────────── */}
      {activeTab === 'data-export' && (
        <div className="space-y-8">
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 rounded-lg bg-green-100 text-green-600">
                <FileSpreadsheet className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Excel Export</h3>
                <p className="text-sm text-gray-600">Export flight data, aircraft, crew, and airports to Excel</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {importConfigs.map(config => (
                <button
                  key={`export-${config.dataType}`}
                  onClick={() => handleExcelExport(config.dataType, config.exportEndpoint, config.exportFilename)}
                  disabled={exporting}
                  className={`p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left ${
                    exporting && activeExport === config.dataType ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <config.icon className={`h-5 w-5 text-${config.color}-600`} />
                    <span className="font-medium text-gray-900">{config.title}</span>
                  </div>
                  {exporting && activeExport === config.dataType ? (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" /><span>Exporting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 text-sm text-green-600">
                      <Download className="h-4 w-4" /><span>Export to Excel</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-gray-700 space-y-1">
              <p>• Exports include all records with proper formatting</p>
              <p>• Files are saved with the current date for easy organisation</p>
              <p>• Compatible with Excel, Google Sheets, and Numbers</p>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Logbook Export ──────────────────────────────────────────── */}
      {activeTab === 'logbook' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left column — Jeppesen settings */}
          <div className="lg:col-span-1 space-y-6">

            {/* Jeppesen settings card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Jeppesen Professional</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                  <select value={format} onChange={e => setFormat(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="jeppesen">Jeppesen Professional</option>
                    <option value="faa" disabled>FAA 8710 (Coming Soon)</option>
                    <option value="easa" disabled>EASA Format (Coming Soon)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {datePresets(setStartDate, setEndDate).map(({ label, fn }) => (
                    <button key={label} onClick={fn} className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 text-gray-600">{label}</button>
                  ))}
                </div>
              </div>
              <div className="mt-6 space-y-3">
                <button onClick={handlePreview} disabled={isPreviewing || !startDate || !endDate}
                  className="w-full flex items-center justify-center px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isPreviewing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading...</> : <><Eye className="h-4 w-4 mr-2" />Preview Data</>}
                </button>
                <button onClick={handleGenerate} disabled={isGenerating || !startDate || !endDate}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isGenerating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Download className="h-4 w-4 mr-2" />Generate & Download PDF</>}
                </button>
              </div>
              <p className="text-xs text-gray-500 italic mt-4">Tip: the cover prints first, so printing double-sided puts each left page opposite its right page in a binder.</p>
            </div>
          </div>

          {/* Right column — preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Logbook Preview</h2>
              {!previewData ? (
                <div className="text-center py-16">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select a date range and click "Preview Data" to see your logbook summary</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Total Flights', value: previewData.flightCount, color: 'blue' },
                      { label: 'Total Hours',   value: formatHours(previewData.totals.page.totalTime), color: 'green' },
                      { label: 'PIC Hours',     value: formatHours(previewData.totals.page.pic), color: 'purple' },
                      { label: 'Landings',      value: previewData.totals.page.dayLandings + previewData.totals.page.nightLandings, color: 'orange' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className={`bg-${color}-50 rounded-lg p-4`}>
                        <div className={`text-2xl font-bold text-${color}-600`}>{value}</div>
                        <div className="text-sm text-gray-600">{label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Time Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        ['Night', previewData.totals.page.night],
                        ['Actual Inst', previewData.totals.page.actualInst],
                        ['Cross Country', previewData.totals.page.crossCountry],
                        ['SIC', previewData.totals.page.sic],
                        ['Day Landings', previewData.totals.page.dayLandings],
                        ['Night Landings', previewData.totals.page.nightLandings],
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between items-center py-2 border-b">
                          <span className="text-sm text-gray-600">{label}</span>
                          <span className="text-sm font-medium">{typeof val === 'number' && val % 1 !== 0 ? formatHours(val) : val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Flights (first 10)</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {['Date','Aircraft','Route','Total','PIC'].map(h => (
                              <th key={h} className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase ${h==='Total'||h==='PIC' ? 'text-right' : 'text-left'}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {previewData.flights.slice(0,10).map((f, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm text-gray-900">{new Date(f.date).toLocaleDateString()}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{f.aircraft}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{f.route}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">{formatHours(f.totalTime)}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">{formatHours(f.pic)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {previewData.flights.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2 text-center">and {previewData.flights.length-10} more flights...</p>
                    )}
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-green-900">Ready to Generate</h4>
                      <p className="text-sm text-green-700 mt-1">Click "Generate & Download PDF" to download it.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: FFDO Export ─────────────────────────────────────────────── */}
      {activeTab === 'ffdo' && (
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-lg shadow p-8">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 rounded-lg bg-blue-100">
                <Shield className="h-6 w-6 text-blue-700" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">FFDO Flight Hours Export</h2>
                <p className="text-sm text-gray-500">TSA format for submitting flight hours</p>
              </div>
            </div>

            {/* Format example */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Output Format</p>
              <pre className="font-mono text-sm text-gray-800">{`03/23/2026 609 PHX 2045 SEA 2359`}</pre>
              <p className="text-xs text-gray-500 mt-2">
                <code className="bg-gray-200 px-1 rounded">DATE FLIGHT# DEP DEPTIME ARR ARRTIME</code>
              </p>
            </div>

            {/* Date pickers */}
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={ffdoStartDate} onChange={e => setFfdoStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={ffdoEndDate} onChange={e => setFfdoEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {/* Quick presets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quick Select</label>
                <div className="grid grid-cols-4 gap-2">
                  {datePresets(setFfdoStartDate, setFfdoEndDate).map(({ label, fn }) => (
                    <button key={label} onClick={fn}
                      className="px-2 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 text-gray-600 text-center">
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Notes */}
            <ul className="text-xs text-gray-500 space-y-1 mb-6">
              <li>• Only flights with a flight number and scheduled times are included</li>
              <li>• Carrier prefix (e.g. "AS") stripped automatically</li>
              <li>• Leading ICAO "K" stripped from airport codes (KSEA → SEA)</li>
              <li>• Times are scheduled local departure/arrival in HHMM format</li>
            </ul>

            {/* Generate button */}
            <button
              onClick={handleFfdoExport}
              disabled={isGeneratingFfdo || !ffdoStartDate || !ffdoEndDate}
              className="w-full flex items-center justify-center px-4 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGeneratingFfdo
                ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Generating...</>
                : <><Shield className="h-5 w-5 mr-2" />Generate FFDO Export</>
              }
            </button>
          </div>
        </div>
      )}

      {/* Schedule Import Modal */}
      <ScheduleImportModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        parsedFlights={parsedFlights}
        onImport={handleConfirmedImport}
      />
    </div>
  );
};

export default DataManager;